import {
  ref, reactive, onBeforeUnmount, getCurrentInstance,
} from 'vue';

import { extract as extractErrorMessage } from '@/utils/message';
import store from '@/stores';

// Só o que é idêntico entre os hosts de listagem; métodos que dependem de
// loadingMixin/roleManagerMixin ficam locais (setup() não tem `this`).
export default function useProposalListActions({ modal, beforeOpenModal } = {}) {
  const instance = getCurrentInstance();
  const message = ref(null);
  const selectRowOptions = reactive({
    selectAll: false,
    selected: [],
  });
  const proposal = reactive({ list: [] });
  const pagination = reactive({ data: {} });

  const setMessage = (value) => {
    message.value = value;
  };

  const handlerError = (err) => {
    setMessage(extractErrorMessage(err));
    pagination.data = {};
    proposal.list = [];
  };

  const toggleSelectRow = (proposalId) => {
    const { selected } = selectRowOptions;
    const isSelected = selected.find((row) => row === proposalId);

    if (isSelected) {
      const selectedIndex = selected.indexOf(isSelected);
      selected.splice(selectedIndex, 1);
    } else {
      selected.push(proposalId);
    }

    // resolve caveat of length reactivity for nested arrays https://v2.vuejs.org/v2/guide/reactivity
    selected.splice(selected.length);
  };

  const toggleSelectAll = () => {
    const checkboxList = document.querySelectorAll('.row-checkbox');
    selectRowOptions.selectAll = !selectRowOptions.selectAll;
    selectRowOptions.selected = [];

    checkboxList.forEach((elem) => {
      elem.checked = false;
    });

    if (selectRowOptions.selectAll) {
      selectRowOptions.selected = proposal.list.map((item) => item.proposal_id);

      checkboxList.forEach((elem) => {
        elem.checked = true;
      });
    }

    // resolve caveat of length reactivity for nested arrays https://v2.vuejs.org/v2/guide/reactivity
    selectRowOptions.selected.splice(selectRowOptions.selected.length);
  };

  const omitYear = (created) => {
    const [date, hour] = created.split(' ');
    const [day, month] = date.split('/');

    return `${day}/${month} ${hour}`;
  };

  const isTodayPastCreated = (date) => {
    let [splittedDate] = date.split(' ');
    splittedDate = splittedDate.split('/');

    if (splittedDate.length === 2) splittedDate.push('2024');

    const [day, month, year] = splittedDate;

    const created = new Date(year, month - 1, day);
    const today = new Date();

    today.setHours(0, 0, 0, 0);
    created.setHours(0, 0, 0, 0);

    return today.getTime() > created.getTime();
  };

  /**
   * Sem CCB não há operação de crédito na bancarizadora, e portanto não há
   * status a consultar: por isso a listagem mostra o número como texto inerte
   * em vez de botão. Não é gatilho faltando — a modal de status existe para a
   * proposta que já virou CCB.
   */
  const hasCCBCreated = ({ external_proposal_number }) => {
    const status = external_proposal_number?.toLowerCase() || '';
    return status !== 'ccb não gerada';
  };

  const getSubAndResponsible = ({ promoter_name, responsible_name }) => (
    `Sub: ${promoter_name} - Resp: ${responsible_name}`
  );

  const open = ({ modal: modalName, data = null }) => {
    if (beforeOpenModal && beforeOpenModal({ modal: modalName, data })) return;
    if (data !== null) store.dispatch('proposal/SET_PROPOSAL_DATA', data);
    modal[modalName] = true;
  };

  const close = async ({ key, paginate: shouldPaginate = false }) => {
    if (key === 'all') {
      for (const label of Object.keys(modal)) modal[label] = false;
    } else {
      modal[key] = false;
    }

    if (shouldPaginate && instance?.proxy?.paginate) await instance.proxy.paginate();
  };

  onBeforeUnmount(() => {
    store.dispatch('proposal/SET_PROPOSAL_DATA', {});
    if (!store.getters['user/isInternalUser']) store.dispatch('proposal/RESET_FILTERS');
  });

  return {
    message,
    selectRowOptions,
    proposal,
    pagination,
    setMessage,
    handlerError,
    toggleSelectRow,
    toggleSelectAll,
    omitYear,
    isTodayPastCreated,
    hasCCBCreated,
    getSubAndResponsible,
    open,
    close,
  };
}
